import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import toast from 'react-hot-toast';
import { CreditCardIcon, Cog6ToothIcon, BuildingOfficeIcon, UserIcon, UserGroupIcon, GiftIcon } from '@heroicons/react/24/outline';
import ToggleSwitch from '../components/ToggleSwitch';
import TrialAlertTemplateModal from '../components/PlatformDefaults/TrialAlertTemplateModal';

const PLATFORM_DEFAULTS_PATH = '/administration/platform-defaults';
const STORAGE_MAIN_TAB = 'platform_defaults_main_tab';
const STORAGE_SETTINGS_SUB_TAB = 'platform_defaults_settings_sub_tab';

interface PlatformDefaultsForm {
  default_reader_licenses_per_full: number;
  default_reader_invites_personal: number;
  default_max_auditor_licenses: number;
  default_max_devices_per_user?: number;
  default_max_folders_per_user?: number;
  default_max_devices_personal?: number;
  default_max_folders_personal?: number;
  default_trial_days?: number;
  default_trial_full_licenses: number;
  default_cli_enabled?: boolean;
  graceful_license_deactivation_days: number;
  // GOV-195: trial-alert milestones per recipient class.
  // Sign convention: negative = days BEFORE expiry, 0 = expiry day, positive = days AFTER.
  // We store as string in form state (the chip-input is a comma-separated text input)
  // and only convert to number[] | null at save time. Empty string = UNSET.
  trial_alert_offsets_days_company?: string;
  trial_alert_offsets_days_personal?: string;
}

// parseOffsetsArray converts a backend INT[] (from the GET response) into
// the comma-separated string the chip-input edits. NULL/empty arrays render
// as "" so the input shows blank for the unset case.
function parseOffsetsArray(arr: number[] | null | undefined): string {
  if (!arr || arr.length === 0) return '';
  return arr.join(', ');
}

// serializeOffsetsString parses the chip-input text back to number[] | null
// for the PUT payload. Empty string → null (UNSET). Mixed / non-int tokens
// → throws (caller maps to a toast). Leading/trailing whitespace ignored.
function serializeOffsetsString(s: string | undefined): number[] | null {
  if (s === undefined || s === null) return null;
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const tokens = trimmed.split(/[\s,]+/).filter((t) => t.length > 0);
  const out: number[] = [];
  for (const tok of tokens) {
    // Allow negative integers; reject anything else.
    if (!/^-?\d+$/.test(tok)) throw new Error(`invalid offset: "${tok}"`);
    out.push(parseInt(tok, 10));
  }
  return out;
}

// Default settings (Settings tab) — structured form types matching backend JSONB
interface DefaultPolicy {
  file_permissions?: Record<string, boolean>;
  folder_permissions?: Record<string, boolean>;
}
interface DefaultPassword {
  min_length?: number;
  require_uppercase?: boolean;
  require_lowercase?: boolean;
  require_numbers?: boolean;
  require_special_chars?: boolean;
  prohibit_easy_to_guess?: boolean;
  prohibit_sequential_numbers?: boolean;
  max_sequential_numbers?: number;
  prohibit_repeated_characters?: boolean;
  max_repeated_characters?: number;
  prohibit_common_passwords?: boolean;
  prohibit_user_info?: boolean;
  require_password_rotation?: boolean;
  password_rotation_days?: number;
  password_expiration_notification_days?: number;
  [key: string]: unknown;
}
interface DefaultSecurity {
  quick_encrypt_policy_override?: boolean;
  override_screen_watermark_settings?: boolean;
  override_print_watermark_settings?: boolean;
}
interface DefaultDevice {
  allow_revoke_device?: boolean;
  allow_create_cli_token?: boolean;
}

interface DefaultSettingsCompanyForm {
  policy: DefaultPolicy;
  password: DefaultPassword;
  security: DefaultSecurity;
  watermark: Record<string, unknown>;
  notification: Record<string, unknown>;
  asset_protection: Record<string, unknown>;
  device_settings: DefaultDevice;
}
interface DefaultSettingsPersonalForm {
  policy?: DefaultPolicy | null;
  security: DefaultSecurity;
  watermark: Record<string, unknown>;
  notification: Record<string, unknown>;
  asset_protection: Record<string, unknown>;
  device_settings: DefaultDevice;
}

const DEFAULT_COMPANY_FORM: DefaultSettingsCompanyForm = {
  policy: {
    file_permissions: {
      convert_original: true, save_changes: true, copy_paste: true, screen_capture: true,
      screen_watermark: true, print: true, print_watermark: true, share: true,
    },
    folder_permissions: { create: true, copy: true, add: true, move: true, share: true, delete: true },
  },
  password: {
    min_length: 8, require_uppercase: false, require_lowercase: false, require_numbers: false, require_special_chars: false,
    prohibit_easy_to_guess: false, prohibit_sequential_numbers: false, max_sequential_numbers: 3,
    prohibit_repeated_characters: false, max_repeated_characters: 3, prohibit_common_passwords: false, prohibit_user_info: false,
    require_password_rotation: false, password_rotation_days: 90, password_expiration_notification_days: 7,
  },
  security: { quick_encrypt_policy_override: false, override_screen_watermark_settings: false, override_print_watermark_settings: false },
  watermark: {},
  notification: {},
  asset_protection: {},
  device_settings: { allow_revoke_device: true, allow_create_cli_token: true },
};
const DEFAULT_PERSONAL_FORM: DefaultSettingsPersonalForm = {
  policy: null,
  security: { quick_encrypt_policy_override: false, override_screen_watermark_settings: false, override_print_watermark_settings: false },
  watermark: {},
  notification: {},
  asset_protection: {},
  device_settings: { allow_revoke_device: true, allow_create_cli_token: true },
};

function buildCompanyPayload(f: DefaultSettingsCompanyForm): Record<string, unknown> {
  return {
    policy: f.policy,
    password: f.password,
    security: f.security,
    watermark: f.watermark,
    notification: f.notification,
    asset_protection: f.asset_protection,
    device_settings: f.device_settings,
  };
}
function buildPersonalPayload(f: DefaultSettingsPersonalForm): Record<string, unknown> {
  return {
    policy: f.policy ?? undefined,
    security: f.security,
    watermark: f.watermark,
    notification: f.notification,
    asset_protection: f.asset_protection,
    device_settings: f.device_settings,
  };
}

function parseCompanyFormFromApi(data: Record<string, unknown> | null | undefined): DefaultSettingsCompanyForm {
  if (!data) return DEFAULT_COMPANY_FORM;
  const policyRaw = (data.policy as Record<string, unknown>) || {};
  const policy: DefaultPolicy = {
    file_permissions: { ...DEFAULT_COMPANY_FORM.policy.file_permissions, ...(policyRaw.file_permissions as Record<string, boolean>) },
    folder_permissions: { ...DEFAULT_COMPANY_FORM.policy.folder_permissions, ...(policyRaw.folder_permissions as Record<string, boolean>) },
  };
  const password: DefaultPassword = { ...DEFAULT_COMPANY_FORM.password, ...(data.password as Record<string, unknown>) };
  const security: DefaultSecurity = { ...DEFAULT_COMPANY_FORM.security, ...(data.security as Record<string, unknown>) };
  return {
    policy,
    password,
    security,
    watermark: (data.watermark as Record<string, unknown>) ?? {},
    notification: (data.notification as Record<string, unknown>) ?? {},
    asset_protection: (data.asset_protection as Record<string, unknown>) ?? {},
    device_settings: { ...DEFAULT_COMPANY_FORM.device_settings, ...(data.device_settings as Record<string, unknown>) },
  };
}
function parsePersonalFormFromApi(data: Record<string, unknown> | null | undefined): DefaultSettingsPersonalForm {
  if (!data) return DEFAULT_PERSONAL_FORM;
  const policy = (data.policy as DefaultPolicy | null | undefined) ?? null;
  const security: DefaultSecurity = { ...DEFAULT_PERSONAL_FORM.security, ...(data.security as Record<string, unknown>) };
  return {
    policy,
    security,
    watermark: (data.watermark as Record<string, unknown>) ?? {},
    notification: (data.notification as Record<string, unknown>) ?? {},
    asset_protection: (data.asset_protection as Record<string, unknown>) ?? {},
    device_settings: { ...DEFAULT_PERSONAL_FORM.device_settings, ...(data.device_settings as Record<string, unknown>) },
  };
}

const AdministrationPlatformDefaults: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('platformDefaults');
  const queryClient = useQueryClient();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  usePageTitle(t('title'), t('description'));

  const [mainTab, setMainTabState] = useState<'subscriptions' | 'settings'>('subscriptions');
  const [settingsSubTab, setSettingsSubTabState] = useState<'company' | 'personal'>('company');

  // Restore tab from localStorage when on this page (persist on refresh). Cleanup when leaving is done by usePlatformDefaultsTabCleanup in App.
  useEffect(() => {
    if (location.pathname !== PLATFORM_DEFAULTS_PATH) return;
    const savedMain = localStorage.getItem(STORAGE_MAIN_TAB);
    const savedSub = localStorage.getItem(STORAGE_SETTINGS_SUB_TAB);
    if (savedMain === 'subscriptions' || savedMain === 'settings') setMainTabState(savedMain);
    if (savedSub === 'company' || savedSub === 'personal') setSettingsSubTabState(savedSub);
  }, [location.pathname]);

  const setMainTab = useCallback((tab: 'subscriptions' | 'settings') => {
    setMainTabState(tab);
    if (location.pathname === PLATFORM_DEFAULTS_PATH) localStorage.setItem(STORAGE_MAIN_TAB, tab);
  }, [location.pathname]);

  const setSettingsSubTab = useCallback((tab: 'company' | 'personal') => {
    setSettingsSubTabState(tab);
    if (location.pathname === PLATFORM_DEFAULTS_PATH) localStorage.setItem(STORAGE_SETTINGS_SUB_TAB, tab);
  }, [location.pathname]);

  const [form, setForm] = useState<Partial<PlatformDefaultsForm>>({});
  // GOV-195: controls the "Edit trial alert email" modal.
  const [trialAlertModalOpen, setTrialAlertModalOpen] = useState(false);
  const initialRef = useRef<{ form: Partial<PlatformDefaultsForm> } | null>(null);

  // Default settings (Company / Personal) for Settings tab — structured form state
  const [companyDefaultForm, setCompanyDefaultForm] = useState<DefaultSettingsCompanyForm>(DEFAULT_COMPANY_FORM);
  const [personalDefaultForm, setPersonalDefaultForm] = useState<DefaultSettingsPersonalForm>(DEFAULT_PERSONAL_FORM);
  const settingsInitialRef = useRef<{ company: string; personal: string } | null>(null);
  const companyFormRef = useRef<DefaultSettingsCompanyForm>(companyDefaultForm);
  const personalFormRef = useRef<DefaultSettingsPersonalForm>(personalDefaultForm);
  companyFormRef.current = companyDefaultForm;
  personalFormRef.current = personalDefaultForm;
  const companyMutateRef = useRef<(payload: Record<string, unknown>) => void>(() => {});
  const personalMutateRef = useRef<(payload: Record<string, unknown>) => void>(() => {});

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-defaults'],
    queryFn: () => apiService.getPlatformDefaults(),
    enabled: user?.role === 'sysadmin',
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Parameters<typeof apiService.updatePlatformDefaults>[0]) =>
      apiService.updatePlatformDefaults(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-defaults'] });
      toast.success(t('toast.updated'));
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      toast.error(err.response?.data?.error || err.message || 'Failed to update.');
    },
  });
  const platformMutateRef = useRef<(updates: Parameters<typeof apiService.updatePlatformDefaults>[0]) => void>(() => {});
  platformMutateRef.current = updateMutation.mutate;

  const { data: companyDefaultData, isLoading: companyDefaultLoading } = useQuery({
    queryKey: ['default-settings-company'],
    queryFn: () => apiService.getDefaultSettingsCompany(),
    enabled: user?.role === 'sysadmin' && mainTab === 'settings' && settingsSubTab === 'company',
  });

  const { data: personalDefaultData, isLoading: personalDefaultLoading } = useQuery({
    queryKey: ['default-settings-personal'],
    queryFn: () => apiService.getDefaultSettingsPersonal(),
    enabled: user?.role === 'sysadmin' && mainTab === 'settings' && settingsSubTab === 'personal',
  });

  const updateCompanyDefaultMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiService.updateDefaultSettingsCompany(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-settings-company'] });
      toast.success('Default company settings updated.');
      if (settingsInitialRef.current) settingsInitialRef.current.company = JSON.stringify(companyFormRef.current);
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      toast.error(err.response?.data?.error || err.message || 'Failed to update.');
    },
  });

  const updatePersonalDefaultMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiService.updateDefaultSettingsPersonal(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-settings-personal'] });
      toast.success('Default personal settings updated.');
      if (settingsInitialRef.current) settingsInitialRef.current.personal = JSON.stringify(personalFormRef.current);
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      toast.error(err.response?.data?.error || err.message || 'Failed to update.');
    },
  });

  // Keep mutate refs updated so setOnSave effect doesn't depend on mutation objects (avoids infinite PUT loop)
  companyMutateRef.current = updateCompanyDefaultMutation.mutate;
  personalMutateRef.current = updatePersonalDefaultMutation.mutate;

  useEffect(() => {
    if (data) {
      const formValues: Partial<PlatformDefaultsForm> = {
        default_reader_licenses_per_full: data.default_reader_licenses_per_full,
        default_reader_invites_personal: data.default_reader_invites_personal,
        default_max_auditor_licenses: data.default_max_auditor_licenses ?? 0,
        default_max_devices_per_user: data.default_max_devices_per_user,
        default_max_folders_per_user: data.default_max_folders_per_user,
        default_max_devices_personal: data.default_max_devices_personal,
        default_max_folders_personal: data.default_max_folders_personal,
        default_trial_days: data.default_trial_days,
        default_trial_full_licenses: data.default_trial_full_licenses,
        default_cli_enabled: data.default_cli_enabled ?? false,
        graceful_license_deactivation_days: data.graceful_license_deactivation_days,
        trial_alert_offsets_days_company: parseOffsetsArray(data.trial_alert_offsets_days_company),
        trial_alert_offsets_days_personal: parseOffsetsArray(data.trial_alert_offsets_days_personal),
      };
      setForm(formValues);
      initialRef.current = { form: formValues };
    }
  }, [data]);

  const isDirty = useMemo(() => {
    if (!initialRef.current) return false;
    const a = initialRef.current;
    return (
      a.form.default_reader_licenses_per_full !== form.default_reader_licenses_per_full ||
      a.form.default_reader_invites_personal !== form.default_reader_invites_personal ||
      a.form.default_max_auditor_licenses !== form.default_max_auditor_licenses ||
      a.form.default_max_devices_per_user !== form.default_max_devices_per_user ||
      a.form.default_max_folders_per_user !== form.default_max_folders_per_user ||
      a.form.default_max_devices_personal !== form.default_max_devices_personal ||
      a.form.default_max_folders_personal !== form.default_max_folders_personal ||
      a.form.default_trial_full_licenses !== form.default_trial_full_licenses ||
      a.form.default_cli_enabled !== form.default_cli_enabled ||
      a.form.default_trial_days !== form.default_trial_days ||
      a.form.graceful_license_deactivation_days !== form.graceful_license_deactivation_days ||
      (a.form.trial_alert_offsets_days_company ?? '') !== (form.trial_alert_offsets_days_company ?? '') ||
      (a.form.trial_alert_offsets_days_personal ?? '') !== (form.trial_alert_offsets_days_personal ?? '')
    );
  }, [form]);
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;
  const formRef = useRef<Partial<PlatformDefaultsForm>>({});
  formRef.current = form;

  const settingsTabDirty = Boolean(
    settingsSubTab === 'company'
      ? settingsInitialRef.current && JSON.stringify(companyDefaultForm) !== settingsInitialRef.current.company
      : settingsInitialRef.current && JSON.stringify(personalDefaultForm) !== settingsInitialRef.current.personal
  );

  // Header Save and dirty state: Subscriptions tab vs Settings tab
  useEffect(() => {
    const dirty = mainTab === 'subscriptions' ? isDirty : settingsTabDirty;
    setHasUnsavedChanges(dirty);
  }, [mainTab, isDirty, settingsTabDirty, setHasUnsavedChanges]);

  useEffect(() => {
    if (mainTab === 'subscriptions') {
      // Use refs so callback identity is stable and we avoid context update loops.
      setOnSave(() => () => {
        if (!isDirtyRef.current) return;
        const f = formRef.current;
        // GOV-195: parse the chip-input strings into number[] | null for the PUT.
        // Validation errors throw; toast + abort.
        let coOffsets: number[] | null | undefined;
        let peOffsets: number[] | null | undefined;
        try {
          coOffsets = f.trial_alert_offsets_days_company !== undefined
            ? serializeOffsetsString(f.trial_alert_offsets_days_company)
            : undefined;
          peOffsets = f.trial_alert_offsets_days_personal !== undefined
            ? serializeOffsetsString(f.trial_alert_offsets_days_personal)
            : undefined;
        } catch (err: any) {
          toast.error(`Trial-alert offsets: ${err.message}`);
          return;
        }
        platformMutateRef.current({
          default_reader_licenses_per_full: f.default_reader_licenses_per_full,
          default_reader_invites_personal: f.default_reader_invites_personal,
          default_max_auditor_licenses: f.default_max_auditor_licenses ?? 0,
          default_max_devices_per_user: f.default_max_devices_per_user,
          default_max_folders_per_user: f.default_max_folders_per_user,
          default_max_devices_personal: f.default_max_devices_personal,
          default_max_folders_personal: f.default_max_folders_personal,
          default_trial_days: f.default_trial_days,
          default_trial_full_licenses: f.default_trial_full_licenses,
          default_cli_enabled: f.default_cli_enabled,
          graceful_license_deactivation_days: f.graceful_license_deactivation_days,
          trial_alert_offsets_days_company: coOffsets,
          trial_alert_offsets_days_personal: peOffsets,
        });
      });
    } else {
      // React setState(fn) calls fn(prevState) as updater — so we must return the handler, not run it
      setOnSave(() => () => {
        if (settingsSubTab === 'company') {
          companyMutateRef.current(buildCompanyPayload(companyFormRef.current));
        } else {
          personalMutateRef.current(buildPersonalPayload(personalFormRef.current));
        }
      });
    }
    return () => setOnSave(null);
    // Refs used for form state so we avoid deps that would cause loop
  }, [mainTab, settingsSubTab, setOnSave]);

  useEffect(() => {
    const submitting =
      mainTab === 'subscriptions'
        ? updateMutation.isPending
        : updateCompanyDefaultMutation.isPending || updatePersonalDefaultMutation.isPending;
    setIsSubmitting(submitting);
  }, [mainTab, updateMutation.isPending, updateCompanyDefaultMutation.isPending, updatePersonalDefaultMutation.isPending, setIsSubmitting]);

  // Sync default settings form when data loads
  useEffect(() => {
    if (companyDefaultData) {
      const parsed = parseCompanyFormFromApi(companyDefaultData as Record<string, unknown>);
      setCompanyDefaultForm(parsed);
      if (!settingsInitialRef.current) settingsInitialRef.current = { company: '', personal: '' };
      settingsInitialRef.current.company = JSON.stringify(parsed);
    }
  }, [companyDefaultData]);

  useEffect(() => {
    if (personalDefaultData) {
      const parsed = parsePersonalFormFromApi(personalDefaultData as Record<string, unknown>);
      setPersonalDefaultForm(parsed);
      if (!settingsInitialRef.current) settingsInitialRef.current = { company: '', personal: '' };
      settingsInitialRef.current.personal = JSON.stringify(parsed);
    }
  }, [personalDefaultData]);

  // GOV-195: value type widened to include string for the two trial-alert
  // offsets chip-inputs (stored as comma-separated text in form state;
  // serialized to number[] | null at save time).
  const update = (key: keyof PlatformDefaultsForm, value: number | string | boolean | undefined) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (!user) return null;
  if (user.role !== 'sysadmin') {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Main tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          type="button"
          onClick={() => setMainTab('subscriptions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
            mainTab === 'subscriptions'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <CreditCardIcon className="w-4 h-4" />
          {t('tabs.subscriptions')}
        </button>
        <button
          type="button"
          onClick={() => setMainTab('settings')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
            mainTab === 'settings'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Cog6ToothIcon className="w-4 h-4" />
          {t('tabs.settings')}
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-300 mb-4">
          {t('loadError')}
        </div>
      )}

      {mainTab === 'subscriptions' && !isLoading && data && (
        <div className="space-y-8">
          {/* Subscriptions & licenses */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                <CreditCardIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('sections.subscriptionsAndLicenses')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('sections.subscriptionsSubtitle')}</p>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Reader invites */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <UserGroupIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{t('sections.readerInvites')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultReaderLicensesPerFull')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_reader_licenses_per_full ?? ''}
                      onChange={(e) => update('default_reader_licenses_per_full', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultReaderLicensesPerFullHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultReaderInvitesPersonal')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_reader_invites_personal ?? ''}
                      onChange={(e) => update('default_reader_invites_personal', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Company limits */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <BuildingOfficeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{t('sections.companyLimits')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultMaxDevicesPerUser')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_max_devices_per_user ?? ''}
                      onChange={(e) => { const v = e.target.value; const n = v === '' ? undefined : parseInt(v, 10); update('default_max_devices_per_user', v === '' ? undefined : (Number.isNaN(n) ? form.default_max_devices_per_user : n)); }}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-400"
                      placeholder={t('fields.defaultMaxPlaceholder')}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultMaxDevicesPerUserHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultMaxFoldersPerUser')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_max_folders_per_user ?? ''}
                      onChange={(e) => { const v = e.target.value; const n = parseInt(v, 10); update('default_max_folders_per_user', v === '' ? undefined : (Number.isNaN(n) ? form.default_max_folders_per_user : n)); }}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-400"
                      placeholder={t('fields.defaultMaxPlaceholder')}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultMaxFoldersPerUserHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultMaxAuditorLicenses')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_max_auditor_licenses ?? 0}
                      onChange={(e) => {
                        const v = e.target.value;
                        const n = v === '' ? 0 : parseInt(v, 10);
                        update('default_max_auditor_licenses', Number.isNaN(n) ? form.default_max_auditor_licenses ?? 0 : n);
                      }}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultMaxAuditorLicensesHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Default: CLI enabled</label>
                    <ToggleSwitch
                      checked={!!form.default_cli_enabled}
                      onChange={(v) => update('default_cli_enabled', v)}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Whether new companies get the CLI add-on enabled by default (full-license users can then create CLI tokens). Off by default.
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal limits */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{t('sections.personalLimits')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultMaxDevicesPersonal')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_max_devices_personal ?? ''}
                      onChange={(e) => { const v = e.target.value; const n = parseInt(v, 10); update('default_max_devices_personal', v === '' ? undefined : (Number.isNaN(n) ? form.default_max_devices_personal : n)); }}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-400"
                      placeholder={t('fields.defaultMaxPlaceholder')}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultMaxDevicesPersonalHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultMaxFoldersPersonal')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_max_folders_personal ?? ''}
                      onChange={(e) => { const v = e.target.value; const n = parseInt(v, 10); update('default_max_folders_personal', v === '' ? undefined : (Number.isNaN(n) ? form.default_max_folders_personal : n)); }}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-400"
                      placeholder={t('fields.defaultMaxPlaceholder')}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultMaxFoldersPersonalHelp')}</p>
                  </div>
                </div>
              </div>

              {/* Trial & grace period */}
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <GiftIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{t('sections.trialAndActivation')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultTrialFullLicenses')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_trial_full_licenses ?? ''}
                      onChange={(e) => update('default_trial_full_licenses', e.target.value ? parseInt(e.target.value, 10) : 0)}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.defaultTrialFullLicensesHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.defaultTrialDays')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.default_trial_days ?? ''}
                      onChange={(e) => update('default_trial_days', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-400"
                      placeholder={t('fields.defaultTrialDaysPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fields.gracefulLicenseDeactivation')}</label>
                    <input
                      type="number"
                      min={0}
                      value={form.graceful_license_deactivation_days ?? ''}
                      onChange={(e) => update('graceful_license_deactivation_days', e.target.value ? parseInt(e.target.value, 10) : 0)}
                      className="max-w-[7rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('fields.gracefulLicenseDeactivationHelp')}</p>
                  </div>
                </div>
                {/* GOV-195: trial-alert configuration. Two chip-inputs for the
                    per-recipient-class milestone map + a button that opens
                    the template-editor modal. Both fields accept comma-
                    separated signed integers (e.g. "-7, -3, 0, 1"); empty
                    string serializes to JSON null at save time (UNSET). */}
                <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Trial alerts</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Sign convention: <code>negative</code> = days before expiry,
                    <code> 0</code> = expiry day, <code>positive</code> = days after.
                    Leave empty to disable trial alerts for that class.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Trial alert offsets · Company
                      </label>
                      <input
                        type="text"
                        data-testid="trial-alert-offsets-company-input"
                        value={form.trial_alert_offsets_days_company ?? ''}
                        onChange={(e) => update('trial_alert_offsets_days_company', e.target.value)}
                        placeholder="e.g. -7, -3, 0, 1"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Trial alert offsets · Personal
                      </label>
                      <input
                        type="text"
                        data-testid="trial-alert-offsets-personal-input"
                        value={form.trial_alert_offsets_days_personal ?? ''}
                        onChange={(e) => update('trial_alert_offsets_days_personal', e.target.value)}
                        placeholder="e.g. -3, 0, 1"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setTrialAlertModalOpen(true)}
                      className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Edit trial alert email
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <TrialAlertTemplateModal
            isOpen={trialAlertModalOpen}
            onClose={() => setTrialAlertModalOpen(false)}
          />
        </div>
      )}

      {mainTab === 'settings' && (
        <>
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              type="button"
              onClick={() => setSettingsSubTab('company')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                settingsSubTab === 'company'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400'
              }`}
            >
              <BuildingOfficeIcon className="w-4 h-4" />
              {t('tabs.company')}
            </button>
            <button
              type="button"
              onClick={() => setSettingsSubTab('personal')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                settingsSubTab === 'personal'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400'
              }`}
            >
              <UserIcon className="w-4 h-4" />
              {t('tabs.personal')}
            </button>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
            {settingsSubTab === 'company' && (
              <>
                {(companyDefaultLoading || !companyDefaultData) && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
                  </div>
                )}
                {!companyDefaultLoading && companyDefaultData && (
                  <div className="space-y-6">
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.policy')}</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.filePermissions')}</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(companyDefaultForm.policy?.file_permissions ?? {}).map(([k, v]) => (
                              <label key={k} className="flex items-center gap-1.5 text-sm">
                                <input type="checkbox" checked={!!v} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, policy: { ...prev.policy, file_permissions: { ...prev.policy?.file_permissions, [k]: e.target.checked } } }))} className="rounded border-gray-300 dark:border-gray-600" />
                                <span className="text-gray-700 dark:text-gray-300">{t(`filePermission.${k}`)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.folderPermissions')}</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(companyDefaultForm.policy?.folder_permissions ?? {}).map(([k, v]) => (
                              <label key={k} className="flex items-center gap-1.5 text-sm">
                                <input type="checkbox" checked={!!v} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, policy: { ...prev.policy, folder_permissions: { ...prev.policy?.folder_permissions, [k]: e.target.checked } } }))} className="rounded border-gray-300 dark:border-gray-600" />
                                <span className="text-gray-700 dark:text-gray-300">{t(`folderPermission.${k}`)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.password')}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settingsFields.minLength')}</label>
                          <input type="number" min={1} value={companyDefaultForm.password?.min_length ?? 8} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, min_length: parseInt(e.target.value, 10) || 8 } }))} className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm" />
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.require_uppercase ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, require_uppercase: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.requireUppercase')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.require_lowercase ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, require_lowercase: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.requireLowercase')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.require_numbers ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, require_numbers: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.requireNumbers')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.require_special_chars ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, require_special_chars: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.requireSpecialChars')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.require_password_rotation ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, require_password_rotation: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.requirePasswordRotation')}</span>
                        </div>
                        {(companyDefaultForm.password?.require_password_rotation ?? false) && (
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settingsFields.passwordRotationDays')}</label>
                            <input type="number" min={1} value={companyDefaultForm.password?.password_rotation_days ?? 90} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, password_rotation_days: parseInt(e.target.value, 10) || 90 } }))} className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm" />
                          </div>
                        )}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settingsFields.passwordExpirationNotificationDays')}</label>
                          <input type="number" min={0} value={companyDefaultForm.password?.password_expiration_notification_days ?? 7} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, password_expiration_notification_days: parseInt(e.target.value, 10) ?? 7 } }))} className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm" />
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.prohibit_sequential_numbers ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, prohibit_sequential_numbers: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.prohibitSequentialNumbers')}</span>
                        </div>
                        {(companyDefaultForm.password?.prohibit_sequential_numbers ?? false) && (
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settingsFields.maxSequentialNumbers')}</label>
                            <input type="number" min={1} value={companyDefaultForm.password?.max_sequential_numbers ?? 3} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, max_sequential_numbers: parseInt(e.target.value, 10) || 3 } }))} className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm" />
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.prohibit_repeated_characters ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, prohibit_repeated_characters: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.prohibitRepeatedCharacters')}</span>
                        </div>
                        {(companyDefaultForm.password?.prohibit_repeated_characters ?? false) && (
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settingsFields.maxRepeatedCharacters')}</label>
                            <input type="number" min={1} value={companyDefaultForm.password?.max_repeated_characters ?? 3} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, max_repeated_characters: parseInt(e.target.value, 10) || 3 } }))} className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm" />
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.prohibit_easy_to_guess ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, prohibit_easy_to_guess: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.prohibitEasyToGuess')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.prohibit_common_passwords ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, prohibit_common_passwords: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.prohibitCommonPasswords')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={companyDefaultForm.password?.prohibit_user_info ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, password: { ...prev.password, prohibit_user_info: v } }))} />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.prohibitUserInfo')}</span>
                        </div>
                      </div>
                    </section>
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.security')}</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.quickEncryptOverride')}</span>
                          <ToggleSwitch checked={companyDefaultForm.security?.quick_encrypt_policy_override ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, security: { ...prev.security, quick_encrypt_policy_override: v } }))} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.overrideScreenWatermark')}</span>
                          <ToggleSwitch checked={companyDefaultForm.security?.override_screen_watermark_settings ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, security: { ...prev.security, override_screen_watermark_settings: v } }))} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.overridePrintWatermark')}</span>
                          <ToggleSwitch checked={companyDefaultForm.security?.override_print_watermark_settings ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, security: { ...prev.security, override_print_watermark_settings: v } }))} />
                        </div>
                      </div>
                    </section>
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.device')}</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowRevokeDevice')}</span>
                          <ToggleSwitch checked={companyDefaultForm.device_settings?.allow_revoke_device ?? true} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, device_settings: { ...prev.device_settings, allow_revoke_device: v } }))} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowCreateCliToken')}</span>
                          <ToggleSwitch checked={companyDefaultForm.device_settings?.allow_create_cli_token ?? true} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, device_settings: { ...prev.device_settings, allow_create_cli_token: v } }))} />
                        </div>
                      </div>
                    </section>
                    {/* Notifications: all channel types + license_alert */}
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.notification')}</h3>
                      <div className="space-y-2">
                        {(['send_invitation_email', 'view_request_notification', 'send_approval_email_to_viewer', 'file_access_violation', 'file_shared', 'file_access_revoked', 'file_access_request', 'policy_updated', 'new_device_registered'] as const).map((key) => {
                          const ch = (companyDefaultForm.notification?.[key] as { app?: boolean; email?: boolean }) ?? {};
                          const labelKey = key === 'send_invitation_email' ? 'sendInvitationEmail' : key === 'view_request_notification' ? 'viewRequestNotification' : key === 'send_approval_email_to_viewer' ? 'sendApprovalEmailToViewer' : key === 'file_access_violation' ? 'fileAccessViolation' : key === 'file_shared' ? 'fileShared' : key === 'file_access_revoked' ? 'fileAccessRevoked' : key === 'file_access_request' ? 'fileAccessRequest' : key === 'policy_updated' ? 'policyUpdated' : 'newDeviceRegistered';
                          return (
                            <div key={key} className="flex items-center justify-between flex-wrap gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t(`settingsFields.${labelKey}`)}</span>
                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!ch.app} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, notification: { ...prev.notification, [key]: { ...ch, app: e.target.checked } } }))} className="rounded" /><span>{t('fields.inApp')}</span></label>
                                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!ch.email} onChange={(e) => setCompanyDefaultForm((prev) => ({ ...prev, notification: { ...prev.notification, [key]: { ...ch, email: e.target.checked } } }))} className="rounded" /><span>{t('fields.email')}</span></label>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('settingsFields.licenseAlert')}</p>
                          {(() => {
                            const la = (companyDefaultForm.notification?.license_alert as Record<string, unknown>)?.company as { app?: boolean; email?: boolean; thresholds?: Record<string, unknown> } | undefined;
                            const th = la?.thresholds ?? {};
                            const lc = (th.license_count as Record<string, unknown>) ?? {};
                            const et = (th.expiry_time as Record<string, unknown>) ?? {};
                            const updateCompanyLicenseAlert = (companyPatch: Record<string, unknown>) => setCompanyDefaultForm((prev) => ({
                              ...prev,
                              notification: {
                                ...prev.notification,
                                license_alert: { ...(prev.notification?.license_alert ?? {}), company: { ...(la ?? {}), ...companyPatch } }
                              }
                            }));
                            const updateCompanyLicenseCount = (patch: Record<string, unknown>) => updateCompanyLicenseAlert({
                              thresholds: { ...(la?.thresholds ?? {}), license_count: { ...lc, ...patch }, expiry_time: th.expiry_time ?? et }
                            });
                            const updateCompanyExpiryTime = (patch: Record<string, unknown>) => updateCompanyLicenseAlert({
                              thresholds: { ...(la?.thresholds ?? {}), license_count: th.license_count ?? lc, expiry_time: { ...et, ...patch } }
                            });
                            return (
                              <div className="space-y-2 pl-2">
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!la?.app} onChange={(e) => updateCompanyLicenseAlert({ app: e.target.checked, email: la?.email ?? false, thresholds: la?.thresholds })} className="rounded" /><span>{t('fields.inApp')}</span></label>
                                  <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!la?.email} onChange={(e) => updateCompanyLicenseAlert({ email: e.target.checked, app: la?.app ?? false, thresholds: la?.thresholds })} className="rounded" /><span>{t('fields.email')}</span></label>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="number" min={0} value={(lc.value as number) ?? 20} onChange={(e) => updateCompanyLicenseCount({ enabled: (lc.enabled as boolean) ?? true, value: parseInt(e.target.value, 10) || 20, unit: (lc.unit as string) ?? 'percentage' })} className="w-16 rounded border px-1 py-0.5 text-sm" />
                                  <select value={(lc.unit as string) ?? 'percentage'} onChange={(e) => updateCompanyLicenseCount({ unit: e.target.value })} className="rounded border px-1 py-0.5 text-sm"><option value="count">{t('thresholdUnits.count')}</option><option value="percentage">{t('thresholdUnits.percentage')}</option></select>
                                  <span className="text-xs">{t('settingsFields.licenseCount')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="number" min={0} value={(et.value as number) ?? 14} onChange={(e) => updateCompanyExpiryTime({ value: parseInt(e.target.value, 10) ?? 14, unit: (et.unit as string) ?? 'days', enabled: (et.enabled as boolean) ?? false })} className="w-16 rounded border px-1 py-0.5 text-sm" />
                                  <select value={(et.unit as string) ?? 'days'} onChange={(e) => updateCompanyExpiryTime({ unit: e.target.value })} className="rounded border px-1 py-0.5 text-sm"><option value="days">{t('thresholdUnits.days')}</option><option value="weeks">{t('thresholdUnits.weeks')}</option><option value="months">{t('thresholdUnits.months')}</option></select>
                                  <span className="text-xs">{t('settingsFields.expiryTime')}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </section>
                    {/* Asset protection */}
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.assetProtection')}</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.ownershipTransferRestrictions')}</p>
                          <div className="flex items-center gap-2">
                            <ToggleSwitch checked={((companyDefaultForm.asset_protection?.ownership_transfer_restrictions as Record<string, unknown>)?.enabled as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, ownership_transfer_restrictions: { ...(prev.asset_protection?.ownership_transfer_restrictions ?? {}), enabled: v, allow_only_company_users: ((prev.asset_protection?.ownership_transfer_restrictions as Record<string, unknown>)?.allow_only_company_users as boolean) ?? false } } }))} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.enabled')}</span>
                            <ToggleSwitch checked={((companyDefaultForm.asset_protection?.ownership_transfer_restrictions as Record<string, unknown>)?.allow_only_company_users as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, ownership_transfer_restrictions: { ...(prev.asset_protection?.ownership_transfer_restrictions ?? {}), allow_only_company_users: v, enabled: ((prev.asset_protection?.ownership_transfer_restrictions as Record<string, unknown>)?.enabled as boolean) ?? false } } }))} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowOnlyCompanyUsers')}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.recipientRestrictions')}</p>
                          <div className="flex items-center gap-2">
                            <ToggleSwitch checked={((companyDefaultForm.asset_protection?.recipient_restrictions as Record<string, unknown>)?.enabled as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, recipient_restrictions: { ...(prev.asset_protection?.recipient_restrictions ?? {}), enabled: v, allow_only_company_users: ((prev.asset_protection?.recipient_restrictions as Record<string, unknown>)?.allow_only_company_users as boolean) ?? false } } }))} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.enabled')}</span>
                            <ToggleSwitch checked={((companyDefaultForm.asset_protection?.recipient_restrictions as Record<string, unknown>)?.allow_only_company_users as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, recipient_restrictions: { ...(prev.asset_protection?.recipient_restrictions ?? {}), allow_only_company_users: v, enabled: ((prev.asset_protection?.recipient_restrictions as Record<string, unknown>)?.enabled as boolean) ?? false } } }))} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowOnlyCompanyUsers')}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.keysBackupStrategy')}</p>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <ToggleSwitch checked={((companyDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves as Record<string, unknown>)?.transfer_ownership_to_admin_on_user_leave as boolean ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), when_user_leaves: { ...((prev.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves ?? {}), transfer_ownership_to_admin_on_user_leave: v } } } }))} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.transferOwnershipToAdminOnLeave')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ToggleSwitch checked={((companyDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves as Record<string, unknown>)?.allow_key_backup_on_self_deletion as boolean ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), when_user_leaves: { ...((prev.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves ?? {}), allow_key_backup_on_self_deletion: v } } } }))} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowKeyBackupOnSelfDeletion')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ToggleSwitch checked={((companyDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_file_key_download as boolean) ?? false} disabled={((companyDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_multiple_file_key_download as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), allow_file_key_download: v } } }))} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowFileKeyDownload')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ToggleSwitch checked={((companyDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_multiple_file_key_download as boolean) ?? false} onChange={(v) => setCompanyDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), allow_multiple_file_key_download: v, allow_file_key_download: v ? true : ((prev.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_file_key_download as boolean) ?? false } } }))} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowMultipleFileKeyDownload')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                )}
              </>
            )}
            {settingsSubTab === 'personal' && (
              <>
                {(personalDefaultLoading || !personalDefaultData) && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
                  </div>
                )}
                {!personalDefaultLoading && personalDefaultData && (
                  <div className="space-y-6">
                    {personalDefaultForm.policy && (
                      <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.policy')}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.filePermissions')}</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(personalDefaultForm.policy?.file_permissions ?? {}).map(([k, v]) => (
                                <label key={k} className="flex items-center gap-1.5 text-sm">
                                  <input type="checkbox" checked={!!v} onChange={(e) => setPersonalDefaultForm((prev) => ({ ...prev, policy: prev.policy ? { ...prev.policy, file_permissions: { ...prev.policy?.file_permissions, [k]: e.target.checked } } : null }))} className="rounded border-gray-300 dark:border-gray-600" />
                                  <span className="text-gray-700 dark:text-gray-300">{t(`filePermission.${k}`)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.folderPermissions')}</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(personalDefaultForm.policy?.folder_permissions ?? {}).map(([k, v]) => (
                                <label key={k} className="flex items-center gap-1.5 text-sm">
                                  <input type="checkbox" checked={!!v} onChange={(e) => setPersonalDefaultForm((prev) => ({ ...prev, policy: prev.policy ? { ...prev.policy, folder_permissions: { ...prev.policy?.folder_permissions, [k]: e.target.checked } } : null }))} className="rounded border-gray-300 dark:border-gray-600" />
                                  <span className="text-gray-700 dark:text-gray-300">{t(`folderPermission.${k}`)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                    {/* Personal: Asset protection — keys_backup_strategy only (no transfer; sysadmin-editable) */}
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.assetProtection')}</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settingsFields.keysBackupStrategy', 'Keys backup & export')}</p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowKeyBackupOnSelfDeletion', 'Allow key backup on account deletion')}</span>
                              <ToggleSwitch checked={((personalDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves as Record<string, unknown>)?.allow_key_backup_on_self_deletion as boolean ?? false} onChange={(v) => setPersonalDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), when_user_leaves: { ...((prev.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.when_user_leaves ?? {}), allow_key_backup_on_self_deletion: v } } } }))} />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowFileKeyDownload', 'Allow single file key download')}</span>
                              <ToggleSwitch checked={((personalDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_file_key_download as boolean) ?? false} disabled={((personalDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_multiple_file_key_download as boolean) ?? false} onChange={(v) => setPersonalDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), allow_file_key_download: v } } }))} />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t('settingsFields.allowMultipleFileKeyDownload', 'Allow bulk file key download')}</span>
                              <ToggleSwitch checked={((personalDefaultForm.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_multiple_file_key_download as boolean) ?? false} onChange={(v) => setPersonalDefaultForm((prev) => ({ ...prev, asset_protection: { ...prev.asset_protection, keys_backup_strategy: { ...(prev.asset_protection?.keys_backup_strategy ?? {}), allow_multiple_file_key_download: v, allow_file_key_download: v ? true : ((prev.asset_protection?.keys_backup_strategy as Record<string, unknown>)?.allow_file_key_download as boolean) ?? false } } }))} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                    {/* Personal: Notifications */}
                    <section className="rounded border border-gray-200 dark:border-gray-700 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('settingsSections.notification')}</h3>
                      <div className="space-y-2">
                        {(['send_invitation_email', 'view_request_notification', 'send_approval_email_to_viewer', 'file_access_violation', 'file_shared', 'file_access_revoked', 'file_access_request', 'policy_updated', 'new_device_registered'] as const).map((key) => {
                          const ch = (personalDefaultForm.notification?.[key] as { app?: boolean; email?: boolean }) ?? {};
                          const labelKey = key === 'send_invitation_email' ? 'sendInvitationEmail' : key === 'view_request_notification' ? 'viewRequestNotification' : key === 'send_approval_email_to_viewer' ? 'sendApprovalEmailToViewer' : key === 'file_access_violation' ? 'fileAccessViolation' : key === 'file_shared' ? 'fileShared' : key === 'file_access_revoked' ? 'fileAccessRevoked' : key === 'file_access_request' ? 'fileAccessRequest' : key === 'policy_updated' ? 'policyUpdated' : 'newDeviceRegistered';
                          return (
                            <div key={key} className="flex items-center justify-between flex-wrap gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{t(`settingsFields.${labelKey}`)}</span>
                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!ch.app} onChange={(e) => setPersonalDefaultForm((prev) => ({ ...prev, notification: { ...prev.notification, [key]: { ...ch, app: e.target.checked } } }))} className="rounded" /><span>{t('fields.inApp')}</span></label>
                                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!ch.email} onChange={(e) => setPersonalDefaultForm((prev) => ({ ...prev, notification: { ...prev.notification, [key]: { ...ch, email: e.target.checked } } }))} className="rounded" /><span>{t('fields.email')}</span></label>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('settingsFields.licenseAlert')}</p>
                          {(() => {
                            const la = personalDefaultForm.notification?.license_alert as { app?: boolean; email?: boolean; thresholds?: Record<string, unknown> } | undefined;
                            const th = la?.thresholds ?? {};
                            const lc = (th.license_count as Record<string, unknown>) ?? {};
                            const et = (th.expiry_time as Record<string, unknown>) ?? {};
                            const updatePersonalLicenseAlert = (patch: Record<string, unknown>) => setPersonalDefaultForm((prev) => ({
                              ...prev,
                              notification: {
                                ...prev.notification,
                                license_alert: { ...(prev.notification?.license_alert ?? {}), ...patch }
                              }
                            }));
                            const updatePersonalLicenseCount = (patch: Record<string, unknown>) => updatePersonalLicenseAlert({
                              thresholds: { ...(la?.thresholds ?? {}), license_count: { ...lc, ...patch }, expiry_time: th.expiry_time ?? et }
                            });
                            const updatePersonalExpiryTime = (patch: Record<string, unknown>) => updatePersonalLicenseAlert({
                              thresholds: { ...(la?.thresholds ?? {}), license_count: th.license_count ?? lc, expiry_time: { ...et, ...patch } }
                            });
                            return (
                              <div className="space-y-2 pl-2">
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!la?.app} onChange={(e) => updatePersonalLicenseAlert({ app: e.target.checked, email: la?.email ?? false, thresholds: la?.thresholds })} className="rounded" /><span>{t('fields.inApp')}</span></label>
                                  <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!la?.email} onChange={(e) => updatePersonalLicenseAlert({ email: e.target.checked, app: la?.app ?? false, thresholds: la?.thresholds })} className="rounded" /><span>{t('fields.email')}</span></label>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="number" min={0} value={(lc.value as number) ?? 20} onChange={(e) => updatePersonalLicenseCount({ value: parseInt(e.target.value, 10) || 20, unit: (lc.unit as string) ?? 'percentage', enabled: (lc.enabled as boolean) ?? true })} className="w-16 rounded border px-1 py-0.5 text-sm" />
                                  <select value={(lc.unit as string) ?? 'percentage'} onChange={(e) => updatePersonalLicenseCount({ unit: e.target.value })} className="rounded border px-1 py-0.5 text-sm"><option value="count">{t('thresholdUnits.count')}</option><option value="percentage">{t('thresholdUnits.percentage')}</option></select>
                                  <span className="text-xs">{t('settingsFields.licenseCount')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="number" min={0} value={(et.value as number) ?? 14} onChange={(e) => updatePersonalExpiryTime({ value: parseInt(e.target.value, 10) ?? 14, unit: (et.unit as string) ?? 'days', enabled: (et.enabled as boolean) ?? false })} className="w-16 rounded border px-1 py-0.5 text-sm" />
                                  <select value={(et.unit as string) ?? 'days'} onChange={(e) => updatePersonalExpiryTime({ unit: e.target.value })} className="rounded border px-1 py-0.5 text-sm"><option value="days">{t('thresholdUnits.days')}</option><option value="weeks">{t('thresholdUnits.weeks')}</option><option value="months">{t('thresholdUnits.months')}</option></select>
                                  <span className="text-xs">{t('settingsFields.expiryTime')}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </section>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdministrationPlatformDefaults;
